import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { LockIcon, CrownIcon } from 'lucide-react';
import PremiumDialog from './PremiumDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface PremiumButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'active' | 'outline' | 'destructive' | 'ghost' | 'link' | 'secondary';
  icon?: React.ReactNode;
  minLevel?: 'basic' | 'pro';
  className?: string;
}

const PremiumButton: React.FC<PremiumButtonProps> = ({
  children,
  onClick,
  variant = 'default',
  icon,
  minLevel = 'basic',
  className
}) => {
  const { isPremium, premiumType } = useAuth();
  const [isPremiumDialogOpen, setIsPremiumDialogOpen] = useState(false);

  // Verificar si el usuario tiene acceso a esta funcionalidad premium
  const hasAccess = isPremium && (
    minLevel === 'basic' || 
    (minLevel === 'pro' && premiumType === 'pro')
  );

  // Verificar si el usuario es premium pero necesita un nivel superior
  const needsUpgrade = isPremium && premiumType === 'basic' && minLevel === 'pro';

  const handlePremiumSuccess = () => {
    // Ejecutar el onClick después de suscribirse
    onClick();
  };

  // Si el usuario tiene acceso, mostrar el botón normal
  if (hasAccess) {
    return (
      <Button 
        variant={variant} 
        onClick={onClick}
        className={className}
      >
        {children}
        {icon}
      </Button>
    );
  }

  // Mostrar un botón bloqueado con tooltip
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="default" 
            onClick={() => setIsPremiumDialogOpen(true)}
            className={className}
          >
            {children}
            {needsUpgrade ? (
              <CrownIcon className="h-4 w-4 ml-2 text-yellow-400" />
            ) : (
              <LockIcon className="h-4 w-4 ml-2" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {needsUpgrade ? (
            <p>Requiere suscripción Pro. Haz clic para actualizar.</p>
          ) : (
            <p>Función premium. Haz clic para suscribirte.</p>
          )}
        </TooltipContent>
      </Tooltip>

      {/* Diálogo de suscripción premium */}
      <PremiumDialog 
        isOpen={isPremiumDialogOpen} 
        onOpenChange={setIsPremiumDialogOpen}
        onSuccess={handlePremiumSuccess}
        highlightPro={needsUpgrade}
      />
    </TooltipProvider>
  );
};

export default PremiumButton; 